import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check, DollarSign, Handshake, TrendingUp, Rocket } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { MaskedLines, Reveal, SectionHead } from '../components/motion';
import Ticker from '../components/Ticker';
import LeadModal from '../components/LeadModal';

const IMG_DESK = '/images/office-floor.jpg';
const IMG_COMPANY = '/images/office-wall.jpg';

const BENEFIT_ICONS = [DollarSign, Handshake, TrendingUp, Rocket];
const STAT_COLORS = ['text-green-400', 'text-blue-400', 'text-yellow-400'];

export default function Home() {
  const { t } = useLang();
  const [leadOpen, setLeadOpen] = useState(false);

  const stats = t('home.stats');
  const pillars = t('home.pillars');
  const pillarLinks = ['/selection', '/training', '/selection'];
  const whyItems = t('home.whyItems');
  const benefits = t('home.benefits');
  const aboutStats = t('home.aboutStats');

  return (
    <main data-testid="home-page">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-blue-600/15 blur-[140px] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 mb-8"
            data-testid="hero-overline"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 pulse-dot" />
            <span className="text-xs font-semibold text-blue-300">{t('home.heroOverline')}</span>
          </motion.div>
          <MaskedLines
            lines={t('home.heroLines')}
            colors={['text-white', 'text-blue-400', 'text-green-400']}
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[1.02]"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-2xl mx-auto text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-sub"
          >
            {t('home.heroSub')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto"
          >
            {stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] p-6">
                <p className={`font-display text-3xl sm:text-4xl ${STAT_COLORS[i]}`}>{s.v}</p>
                <p className="text-sm font-semibold text-white mt-2">{s.l}</p>
                <p className="text-xs text-slate-400 mt-1">{s.s}</p>
              </div>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <Link
              data-testid="hero-cta-training"
              to="/training"
              className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-7 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-400 hover:shadow-[0_0_35px_rgba(59,130,246,0.5)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('home.ctaSecondary')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              data-testid="hero-cta-selection"
              to="/selection"
              className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-7 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-green-400 hover:shadow-[0_0_35px_rgba(34,197,94,0.5)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('home.ctaPrimary')}
            </Link>
          </motion.div>
        </div>
      </section>

      <Ticker />

      <section className="py-24 sm:py-32" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('home.pillarsOverline')} title={t('home.pillarsTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.12}>
                <Link
                  to={pillarLinks[i]}
                  data-testid={`pillar-card-${i}`}
                  className="group block h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-8 text-center transition-all duration-300 hover:border-blue-500/60 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-blue-950/50"
                >
                  <span className="font-display text-5xl text-blue-500/25 group-hover:text-blue-400/50 transition-colors">{p.num}</span>
                  <h3 className="mt-4 font-display text-2xl text-white">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-400">
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead overline={t('home.whyOverline')} title={t('home.whyTitle')} />
            <ul className="mt-10 space-y-4">
              {whyItems.map((item, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <li className="flex items-start gap-3 text-slate-300">
                    <span className="mt-1 flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-green-500/15 border border-green-500/40">
                      <Check className="w-3 h-3 text-green-400" />
                    </span>
                    <span className="text-sm sm:text-base">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-xl overflow-hidden border border-slate-700/70">
                <img src={IMG_DESK} alt="ALEF Traders office" className="w-full h-[460px] object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#1E293B] border border-slate-700 rounded-xl px-6 py-5 shadow-xl">
                <p className="font-display text-4xl text-green-400">$50T+</p>
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 mt-1">NYSE + NASDAQ</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('home.benOverline')} title={t('home.benTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.09}>
                  <div data-testid={`benefit-card-${i}`} className="h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-7 text-center transition-all duration-300 hover:border-green-500/50 hover:-translate-y-1">
                    <div className="mx-auto w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="mt-4 font-display text-lg text-white">{b.t}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{b.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-xl border border-blue-500/20 pointer-events-none" />
              <img src={IMG_COMPANY} alt={t('home.aboutName')} className="w-full h-[420px] object-cover rounded-xl border border-slate-700/70" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <SectionHead overline={t('home.aboutOverline')} title={t('home.aboutName')} />
            <p className="mt-3 text-xs font-semibold tracking-widest text-green-400">{t('home.aboutRole')}</p>
            <Reveal delay={0.1}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">{t('home.aboutBio')}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {aboutStats.map((s, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`about-stat-${i}`}>
                    <p className="font-display text-3xl sm:text-4xl text-blue-400">{s.v}</p>
                    <p className="text-[10px] font-semibold tracking-widest text-slate-500 mt-1">{s.l}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 relative overflow-hidden" data-testid="cta-section">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">
              {t('home.ctaTitle')}
            </h2>
            <p className="mt-5 text-blue-100 max-w-xl mx-auto">{t('home.ctaSub')}</p>
            <button
              data-testid="cta-apply-btn"
              onClick={() => setLeadOpen(true)}
              className="mt-9 inline-flex items-center gap-2 rounded-lg bg-white px-9 py-4 text-lg font-semibold text-blue-700 transition-all duration-300 hover:bg-blue-50 hover:shadow-[0_0_45px_rgba(255,255,255,0.35)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('home.ctaBtn')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Reveal>
        </div>
      </section>

      <LeadModal open={leadOpen} onOpenChange={setLeadOpen} source="general" />
    </main>
  );
}
